import { Formik } from "formik";
import * as React from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import styled from "styled-components";
import _ from "lodash";
import { useMappedState } from "redux-react-hook";
import { useLocation } from "react-router-dom";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  Form,
  IFormField,
  IFormOption
} from "src/library/components/Forms";
import Grid from "src/library/components/Hedron";
import CustomerInfoSidebar from "src/core/customers/components/CustomerInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  useDropsApi,
  IDrop
} from "src/core/drops/api/drops.api";
import { useUserApi } from "src/core/users/api/users.api";
import { useCustomer } from "src/core/customers/hooks";
import { PageProps } from "src/pages/Router/routes";
import {
  IRole,
  useRolesApi
} from "src/core/shared/api/roles.api";
import { SiteSettings } from "src/core/shared/store/global/global.reducer";
import { IStore } from "src/core/shared/store";

type CreateCustomerUserProps = {
  customerId: string;
};

const createCustomerUserForm = (terminology: ITerminology, drops: IDrop[], roles: IRole[], defaultDropId?: string, subdomains?: SiteSettings["sso"]["subdomains"]): IFormField[] => {
  const formItems: IFormField[] = [
    {
      type: "input",
      name: "firstName",
      label: "First Name",
      placeholder: "First Name",
      validation: yup
        .string()
        .min(1)
        .required("First Name is required")
    },
    {
      type: "input",
      name: "lastName",
      label: "Last Name",
      placeholder: "Last Name",
      validation: yup
        .string()
        .min(1)
        .required("Last Name is required")
    },
    {
      type: "input",
      name: "email",
      label: "Email",
      placeholder: "Email",
      validation: yup
        .string()
        .email("Please enter a valid email address")
        .required("Email is required")
    },
    {
      type: "select",
      name: "drop",
      label: `Initial ${terminology.drop.singular.text}`,
      placeholder: `Please select a ${terminology.drop.singular.text}`,
      options: drops.map(
        (drop): IFormOption => {
          return {
            value: drop.id.toString(),
            text: `${drop.name} (${drop.branchNumber})`
          };
        }),

      value: defaultDropId,
      validation: yup
        .string()
        .min(1)
        .required(`Please select a ${terminology.drop.singular.text}`)
    },
    {
      type: "select",
      name: "roleId",
      label: "Role",
      placeholder: "Role",
      options: _.map(roles, role => ({
        text: `${role.name}`,
        value: role.id.toString()
      })),
      validation: yup
        .string()
        .min(1)
        .required("Role is required")
    },

    {
      type: "checkbox",
      name: "passwordAuthDisabled",
      label: "Disable password login",
      options: [
        {
          text: "Disable Password Login",
          value: "true"
        }
      ]
    }
  ];

  if (subdomains?.length) {
    formItems.push({
      type: "select",
      name: "useSubdomain",
      label: "Use Subdomain",

      options: subdomains.map(
        (subdomain): IFormOption => {
          return {
            value: subdomain.name,
            text: subdomain.name
          };
        }
      )
    });
  }

  return formItems;
};

/**
 * Main "CreateUser" View
 *
 * @returns
 */
const CreateCustomerUserPage: React.ReactFCWithChildren<PageProps<CreateCustomerUserProps, {customerId: string;}>> = props => {
  const {
    match, terminology, history
  } = props;

  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const location = useLocation<{dropId?: string;}>();
  const { isLoading: loadingCustomer, customer } = useCustomer(customerId);
  const [ createUserResponse, createUser ] = useUserApi("CREATE_USER");
  const [ getDropsResponse, getDrops ] = useDropsApi("LIST_DROPS_BY_CUSTOMER");
  const [ drops, setDrops ] = React.useState<IDrop[] | undefined>();
  const formRef = React.useRef<Formik>(null);
  const [ rolesResponse, listRoles ] = useRolesApi("LIST_ROLES");
  const siteSettings = useMappedState((store: IStore) => store.global.settings?.siteSettings);

  React.useEffect(() => {
    listRoles({ filters: { type: "customer" } });
  }, [ listRoles ]);

  // On mount
  React.useEffect(() => {
    if (!drops) {
      getDrops({
        customerId,
        pagination: {},
        filters: {}
      });
    }
  }, []);

  React.useEffect(() => {
    // On success
    if (getDropsResponse.data) {
      setDrops(getDropsResponse.data.result);
    }

    // On error
    if (getDropsResponse.error) {
      toast.error(`An unexpected error occurred - ${getDropsResponse.error.message}`);
    }
  }, [ getDropsResponse ]);

  React.useEffect(() => {
    // On Success reset the form
    if (createUserResponse.data) {
      if (formRef.current) {
        formRef.current.resetForm();
      }

      if (location?.state?.dropId) { //if coming from drop page, go back to user drops
        history.push(`/admin/customers/${customerId}/drops/${location.state.dropId}/users`);
      } else {
        history.push(`/admin/customers/${customerId}/users/${createUserResponse.data.id}/drops`);
      }
    }

    // On error
    if (createUserResponse.error) {
      toast.error(createUserResponse.error.message);
    }
  }, [
    createUserResponse,
    customerId,
    history,
    location?.state?.dropId
  ]);

  React.useEffect(() => {
    if (rolesResponse.error) {
      toast.error(rolesResponse.error.message);
    }
  }, [ rolesResponse.error ]);

  return (
    <Layout {...props}>
      <MainContentWrapper>
        {!drops || !customer ? (
          <LoadingSpinner type="circular" />
        ) : (
          <FormWrapper>
            <Form
              ref={formRef}
              error={createUserResponse.error ? createUserResponse.error.message : null}
              submitting={createUserResponse.pending}
              fields={createCustomerUserForm(terminology, drops, rolesResponse.data || [], location?.state?.dropId, siteSettings?.sso?.subdomains)}
              onSubmit={({
                firstName, lastName, email, drop, roleId, passwordAuthDisabled, useSubdomain
              }) => {
                const dropId = parseInt(drop, 0);
                const userBranches = drops.filter(drop => drop.id === dropId);
                const userCustomers = [ customer ];

                return createUser({
                  firstName,
                  lastName,
                  email,
                  roleId,
                  defaultCustomerId: customer.id,
                  passwordAuthDisabled: (passwordAuthDisabled as string[]).includes("true"),
                  defaultBranchId: dropId,
                  useSubdomain,
                  userBranches,
                  userCustomers
                });
              }}
              submitButtonText={`Create ${terminology.user.singular.text}`}
            />
          </FormWrapper>
        )}
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerInfoSidebar
          terminology={terminology}
          isLoading={loadingCustomer}
          data={customer}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
  form {
    width: 400px;
  }
`;

export default CreateCustomerUserPage;
